import { useId } from 'react';

import { cn } from '@mds/shared/lib/utils';
import type { AdminRole } from '@mds/shared/types/admin';

import { FieldError } from '@/components/ui/field';

/**
 * Roles as toggle chips, for a new administrator and for replacing an existing
 * one's.
 *
 * Not a Field: a <label> around several buttons would forward every click on
 * it to the first one. So it draws its own error the way Field does, and marks
 * each chip invalid so Save's `check()` lands focus on the first of them.
 */
export default function RolePicker({
  roles,
  value,
  onChange,
  locked,
  label,
  hint,
  error,
  onBlur,
}: {
  roles: AdminRole[];
  value: string[];
  onChange: (next: string[]) => void;
  /** A role that cannot be taken off, and why. */
  locked?: (role: AdminRole, on: boolean) => string | null;
  /** Left out where a heading already names the list. */
  label?: string;
  hint?: string;
  error?: string;
  onBlur?: () => void;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = error ? errorId : hint ? hintId : undefined;

  return (
    <fieldset className="m-0 border-0 p-0" onBlur={onBlur}>
      {label && (
        <legend
          className={cn(
            'font-mono text-sm font-semibold tracking-wider uppercase',
            error ? 'text-danger-text' : 'text-ink-secondary',
          )}
        >
          {label}
        </legend>
      )}
      {hint && !error && (
        <p id={hintId} className="m-0 mt-2 text-base text-ink-muted">
          {hint}
        </p>
      )}
      <ul className={cn('m-0 flex list-none flex-wrap gap-2 p-0', (label || hint) && 'mt-3')}>
        {roles.map((r) => {
          const on = value.includes(r.id);
          const reason = locked?.(r, on) ?? null;
          return (
            <li key={r.id}>
              <button
                type="button"
                disabled={reason !== null}
                aria-pressed={on}
                aria-invalid={error ? true : undefined}
                aria-describedby={describedBy}
                onClick={() =>
                  onChange(on ? value.filter((roleId) => roleId !== r.id) : [...value, r.id])
                }
                className={
                  'rounded-full border px-3.5 py-1.5 text-base transition-colors focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color) disabled:cursor-not-allowed disabled:opacity-60 ' +
                  (on
                    ? 'border-brand-edge bg-brand-wash font-semibold text-brand-strong'
                    : 'border-hairline bg-surface text-ink-muted hover:border-brand-edge hover:text-brand-text')
                }
                title={reason ?? undefined}
              >
                {r.name}
              </button>
            </li>
          );
        })}
      </ul>
      {error && (
        <div className="mt-2">
          <FieldError id={errorId}>{error}</FieldError>
        </div>
      )}
    </fieldset>
  );
}
